<template>
  <div class="barrier-control-page">
    <header class="control-header">
      <div class="eyebrow"><span class="status-dot" />远程道闸控制</div>
      <h1>{{ parkName }}</h1>
      <div class="park-meta">车场编号 {{ parkId || '-' }}</div>
    </header>

    <main class="control-main">
      <div class="safety-note">
        <van-icon name="warning-o" />
        <span>操作会立即下发到现场道闸，请确认车道和车辆情况。</span>
      </div>

      <van-skeleton v-if="loading" title :row="4" />

      <template v-else-if="roadWays.length">
        <section
          v-for="roadWay in roadWays"
          :key="roadWay.laneCode"
          class="lane-card"
        >
          <div class="lane-heading">
            <div>
              <div class="lane-name">{{ roadWay.roadName || '未命名车道' }}</div>
              <div class="lane-code">{{ roadWay.laneCode }}</div>
            </div>
            <div class="direction" :class="directionClass(roadWay.direction)">
              {{ roadWay.direction || '未知' }}
            </div>
          </div>

          <div class="lane-actions">
            <van-button
              class="lane-button"
              color="#4d86e0"
              :loading="isOperating(roadWay.laneCode, 0)"
              :disabled="Boolean(activeOperation)"
              loading-text="开闸中"
              @click="confirmOperation(roadWay, 0)"
            >
              开闸
            </van-button>
            <van-button
              class="lane-button"
              plain
              color="#cf4a4a"
              :loading="isOperating(roadWay.laneCode, 1)"
              :disabled="Boolean(activeOperation)"
              loading-text="关闸中"
              @click="confirmOperation(roadWay, 1)"
            >
              关闸
            </van-button>
          </div>
        </section>
      </template>

      <van-empty v-else description="未查询到可操作车道" />
    </main>
  </div>
</template>

<script>
import {
  getBarrierRoadWays,
  operateBarrier
} from '@/api/modules/parking'
import {
  normalizeRoadWays,
  buildBarrierOperationParams,
  isBarrierOperationSuccessful,
  getBarrierParkName
} from './barrier-control-flow'

export default {
  name: 'BarrierControl',
  data() {
    return {
      parkId: '',
      roadWays: [],
      loading: false,
      activeOperation: ''
    }
  },
  computed: {
    parkName() {
      return getBarrierParkName(this.parkId)
    }
  },
  created() {
    this.parkId = this.$route.query.parkId || ''
    this.loadRoadWays()
  },
  methods: {
    async loadRoadWays() {
      if (!/^\d+$/.test(this.parkId)) {
        this.$toast('车场参数有误，请重新扫码')
        return
      }
      try {
        this.loading = true
        const data = await getBarrierRoadWays({ parkId: this.parkId })
        this.roadWays = normalizeRoadWays(data)
      } catch (error) {
        this.roadWays = []
      } finally {
        this.loading = false
      }
    },
    directionClass(direction) {
      return direction === '入' ? 'inbound' : 'outbound'
    },
    operationKey(laneCode, operType) {
      return `${laneCode}:${operType}`
    },
    isOperating(laneCode, operType) {
      return this.activeOperation === this.operationKey(laneCode, operType)
    },
    async confirmOperation(roadWay, operType) {
      const actionName = operType === 0 ? '开闸' : '关闸'
      try {
        await this.$dialog.confirm({
          title: `确认${actionName}`,
          message: `将对“${roadWay.roadName || roadWay.laneCode}”执行${actionName}，是否继续？`,
          confirmButtonText: actionName,
          confirmButtonColor: operType === 0 ? '#4d86e0' : '#cf4a4a'
        })
      } catch (error) {
        return
      }
      await this.submitOperation(roadWay, operType, actionName)
    },
    async submitOperation(roadWay, operType, actionName) {
      const key = this.operationKey(roadWay.laneCode, operType)
      try {
        this.activeOperation = key
        const params = buildBarrierOperationParams({
          parkId: this.parkId,
          laneCode: roadWay.laneCode,
          operType
        })
        const result = await operateBarrier(params)
        if (!isBarrierOperationSuccessful(result)) {
          throw new Error(`${actionName}失败，请确认现场状态后重试`)
        }
        this.$toast.success(`${actionName}指令已下发`)
      } catch (error) {
        this.$toast(error.message)
      } finally {
        this.activeOperation = ''
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.barrier-control-page {
  min-height: 100vh;
  background: #f3f6fa;
  color: #253047;
}

.control-header {
  padding: 30px 20px 26px;
  color: #fff;
  background: linear-gradient(145deg, #26395d 0%, #416aa8 100%);

  .eyebrow {
    display: flex;
    align-items: center;
    font-size: 12px;
    letter-spacing: 1px;
    opacity: 0.78;
  }

  .status-dot {
    width: 7px;
    height: 7px;
    margin-right: 8px;
    border-radius: 50%;
    background: #70e0a5;
    box-shadow: 0 0 0 4px rgba(112, 224, 165, 0.15);
  }

  h1 {
    margin: 14px 0 6px;
    font-size: 28px;
    line-height: 1.25;
    letter-spacing: 1px;
  }

  .park-meta {
    font-size: 13px;
    opacity: 0.72;
  }
}

.control-main {
  padding: 16px 14px 30px;
}

.safety-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 14px;
  padding: 12px 14px;
  border: 1px solid #f1d7ab;
  border-radius: 8px;
  color: #7b5a25;
  background: #fff8ea;
  font-size: 13px;
  line-height: 1.6;

  .van-icon {
    margin-top: 3px;
  }
}

.lane-card {
  margin-bottom: 12px;
  padding: 18px;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 5px 18px rgba(40, 62, 99, 0.07);
}

.lane-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
}

.lane-name {
  font-size: 17px;
  font-weight: 700;
  line-height: 1.4;
}

.lane-code {
  margin-top: 7px;
  color: #8a94a6;
  font-size: 11px;
  line-height: 1.4;
  word-break: break-all;
}

.direction {
  flex: 0 0 auto;
  min-width: 34px;
  padding: 5px 9px;
  border-radius: 16px;
  text-align: center;
  font-size: 12px;
  font-weight: 700;

  &.inbound {
    color: #26774e;
    background: #e9f8f0;
  }

  &.outbound {
    color: #8a5b1e;
    background: #fff3df;
  }
}

.lane-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 20px;
}

.lane-button {
  width: 100%;
  height: 44px;
  border-radius: 6px;
  font-weight: 700;
}
</style>
